import { msg } from "@omnia/shared/i18n/messages"
import { showOkCancel, showWarning } from "@omnia/shared/react/msgbox"
import { compareArrays, isArrayEmpty } from "@omnia/shared/utils/array"
import { FC, PropsWithChildren } from "react"
import { PlanStatus } from "../types"
import { PlansToValidate, PlanValidationResult, PlanValidationService } from "./types"
import { omniaResolve } from "@omnia/shared/promises"

const msgTaskOrRecordsTitle = "Impacts on tasks or records"
const msgReadOnlyTitle = "Read-only plan"
const msgReadOnlyComplete = "Plan '{{name}}' cannot be updated because it has already been completed."
const msgReadOnlyAbandoned = "Plan '{{name}}' cannot be updated because it has been abandoned."


/**
 * Validates a plan to update by calling the API
 * Displays an ok/cancel dialog if the user should be warned
 * about tasks or records already referencing any of the plans
 * @param svc Validation service
 * @param plans Plans to validate
 * @returns true if the user clicked "Ok", false otherwise
 */
export function showPlanUpdateValidationDialog(svc: PlanValidationService, plans: PlansToValidate): PromiseLike<boolean> {

    return svc.validate(plans)
        .then(status => showDialog(plans, status))

}

function showDialog(request: PlansToValidate, status: PlanValidationResult[]): PromiseLike<boolean> {

    // First check the status of each plan
    // This is done in a simple loop which stops at the first offending plan found,
    // because scenarios which involve checking multiple plans would only concern
    // spray plans and v2 nutrition plans, which should be locked upfront anyway

    for (const s of status) {

        const readOnlyMsg = isStatusReadOnly(s.status)

        if (readOnlyMsg) {
            return showWarning(msg(readOnlyMsg, { name: s.name }), msgReadOnlyTitle).then(() => false)
        }

    }

    const warnings = status
        .map(s => {

            // Check if the cropping has changed, do nothing otherwise

            if (!hasCroppingChanged(request, s)) {
                return
            }

            const tasks = isArrayEmpty(s.tasks) ? null : <Tasks tasks={s.tasks} />
            const records = isArrayEmpty(s.records) ? null : <Records records={s.records} />

            if (!(records || tasks)) {
                return
            }

            const warnings = <>
                {tasks}
                {
                    tasks && records &&
                    <hr style={{ margin: "1.25rem -0.5rem" }} />
                }
                {records}
            </>

            return {
                planName: s.name,
                warning: warnings
            }

        })
        .filter(w => Boolean(w))

    if (isArrayEmpty(warnings)) {
        return omniaResolve(true)
    }

    const content = (request.plans.length === 1)
        ? warnings[0].warning
        : <>
            {warnings.map((w, index) => <PlanWarnings key={index} planName={w.planName}>
                {w.warning}
            </PlanWarnings>)}
        </>

    return showOkCancel(content, msg(msgTaskOrRecordsTitle))

}

function isStatusReadOnly(status: PlanStatus) {

    return (status === PlanStatus.Complete)
        ? msgReadOnlyComplete
        : (status === PlanStatus.Abandoned)
            ? msgReadOnlyAbandoned
            : undefined

}

const PlanWarnings: FC<{ planName: string }> = ({ planName, children }) => {

    return <Section title={planName}>
        {children}
    </Section>

}

function Tasks({ tasks }: { tasks: PlanValidationResult["tasks"] }) {

    return <Section title="Tasks">
        {
            tasks.length === 1
                ? <SingleTask task={tasks[0]} />
                : <MultipleTasks tasks={tasks} />
        }
    </Section>

}

function SingleTask({ task }: { task: PlanValidationResult["tasks"][number] }) {

    return <>

        <p>
            This plan is already part of a task named '<strong>{task.name}</strong>'
        </p>

        <p>
            This task will be updated automatically to reflect any change made to the plan's cropping.
        </p>

        <p>
            Please use the Task Manager feature of the Field Diary module to review the updated task.
        </p>

    </>
}


function MultipleTasks({ tasks }: { tasks: PlanValidationResult["tasks"] }) {

    return <>

        <p>
            This plan is already part of the following tasks:
        </p>

        <ul style={{ padding: "0 1rem" }}>
            {tasks.map(t => <li key={t.key}>&ndash;&nbsp;{t.name}</li>)}
        </ul>

        <p>
            Since this plan is part of multiple tasks, the tasks cannot be updated automatically.
            Please use the Task Manager feature of the Field Diary module to update those tasks if required.
        </p>

    </>

}

function Records({ records }: { records: PlanValidationResult["records"] }) {

    return <Section title="Records">

        <p>This plan already has one or more records.</p>

        <p>Records documenting this plan will not be updated automatically to reflect changes made to this plan.</p>

        <p>
            Please use the Records feature of the Field Diary module if you need to update any record for this plan.
        </p>

    </Section>

}

function Section({ title, children }: PropsWithChildren<{ title: string }>) {

    return <div>

        <h3 style={{ fontSize: 14, fontWeight: 600, margin: "0 0 1rem 0" }}>
            {title}
        </h3>

        <div style={{ padding: "0 0.5rem" }}>
            {children}
        </div>

    </div>
}

function hasCroppingChanged(request: PlansToValidate, plan: PlanValidationResult) {

    const updatedPlan = request.plans.find(p => p.key === plan.key)

    if (!updatedPlan) {
        return true
    }

    const a = updatedPlan.cropping.map(c => c.croppingId)
    const b = plan.cropping.map(c => c.croppingId)

    return !compareArrays(a, b, false)

}
